import {
  type ButtonProps,
  type CopyButtonProps,
  type DropdownMenuItemProps,
} from '@wandb/components';

import {useMutableBox} from '../../util/hooks/useMutableBox';
import {useMutableFn} from '../../util/hooks/useMutableFn';
import {Analytics} from './Analytics';
import {type EventData} from './types';

// Snoop the event type from @wandb/components:Button
type PressHandler = Required<ButtonProps>['onPress'];
type PressEvent = Parameters<PressHandler>[0];

export function useOnPressAnalytics(
  eventName: string,
  properties: EventData,
  andThen?: (e: PressEvent) => void
): PressHandler {
  const getProperties = useMutableBox({eventName, properties});
  const thenFn = useMutableFn((e: PressEvent) => {
    if (andThen) {
      andThen(e);
    } else {
      e.continuePropagation();
    }
  });

  return (e: PressEvent) => {
    const {eventName, properties} = getProperties();
    Analytics.track(eventName, properties);
    thenFn(e);
  };
}

type CopyHandler = Required<CopyButtonProps>['onCopy'];

export function useOnCopyAnalytics(
  eventName: string,
  properties: EventData
): CopyHandler {
  const getProperties = useMutableBox({eventName, properties});

  return () => {
    const {eventName, properties} = getProperties();
    Analytics.track(eventName, properties);
  };
}

type ActionHandler = Required<DropdownMenuItemProps>['onAction'];

export function useOnActionAnalytics(
  eventName: string,
  properties: EventData,
  andThen?: () => void
): ActionHandler {
  const getProperties = useMutableBox({eventName, properties});
  const thenFn = useMutableFn(() => {
    if (andThen) {
      andThen();
    }
  });

  return () => {
    const {eventName, properties} = getProperties();
    Analytics.track(eventName, properties);
    thenFn();
  };
}
